import Bugsnag from "@bugsnag/js";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { BooleanParam, useQueryParams } from "use-query-params";

import { addToast } from "../../../Store/Actions";
import { setShowLinkedAccountTooltip } from "../../../Store/Actions/calendarActions";
import amahaLogo from "../../../assets/calendar/amaha-logo.webp";
import childrenFirstLogo from "../../../assets/calendar/children-first-logo.webp";
import googleLogo from "../../../assets/calendar/google-logo.webp";
import link from "../../../assets/calendar/link-logo.webp";
import axiosInstance from "../../../axiosInstance";
import { trackEvent } from "../../../helperFunctions";
import URL from "../../../urls";
import { CF_PROVIDER_ROLE, websiteURL } from "../../../variable";
import { Button, IconButton } from "../../common/Buttons";
import { Case, Switch } from "../../common/ConditionalRendering";
import FlexBox from "../../common/FlexBox";
import { Body2, H3, Support } from "../../common/Headings";
import Loader from "../../common/Loader";
import { Modal } from "../../common/Modal";
import {
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  ERROR_RED_500,
} from "../../common/colors";
import AccountAvatar from "./AccountAvatar";
import RemoveAccountModal from "./RemoveAccountModal";

const Header = styled(FlexBox)`
  justify-content: space-between;
  padding: 1rem 1rem 1rem 1.5rem;
  border-bottom: 1px solid ${DAVYS_GREY_400};
`;

const Content = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  padding: 1.5rem;
`;

const Close = styled(FiX)`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.25rem;
  stroke-width: 3;
  color: ${DAVYS_GREY_800};
  cursor: pointer;
`;

const FlexWrapper = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  row-gap: 1rem;
  justify-content: center;
  align-items: center;
  align-self: center;
`;

const AccountDetails = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  row-gap: 0.25rem;
`;

const LinkedAccountsModal = () => {
  const dispatch = useDispatch();

  const user = useSelector(state => state.auth.user);
  const providerProfile = useSelector(state => state.provider.providerProfile);
  const isCfProvider = providerProfile?.roles?.some(role =>
    CF_PROVIDER_ROLE.includes(role)
  );
  const { googleClientId, googleClientSecret } = useSelector(state => ({
    googleClientId: state.auth.googleAuth.clientId,
    googleClientSecret: state.auth.googleAuth.clientSecret,
  }));

  const [queryParams, setQueryParams] = useQueryParams({
    showLinkedAccountsModal: BooleanParam,
    reloadLinkedAccounts: BooleanParam,
  });

  const { reloadLinkedAccounts } = queryParams;

  const [loading, setLoading] = useState(true);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [reloadAccounts, setReloadAccounts] = useState(false);
  const [showRemoveCta, setShowRemoveCta] = useState(false);
  const [showRemoveAccountModal, setShowRemoveAccountModal] = useState(false);

  const handleSuccess = async codeResponse => {
    const credentials = await getCredentials(codeResponse.code);
    if (!credentials) return;
    const userInfo = await getUserInfo(credentials.access_token);
    if (!userInfo) return;
    addAccount(userInfo, credentials);
    closeModal();
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: handleSuccess,
    onError: errorResponse => {
      dispatch(
        addToast({
          msg: "Error linking account",
          appearance: "error",
          autoDismiss: true,
        })
      );
      setShowAddAccount(true);
    },
    scope: "https://www.googleapis.com/auth/calendar",
  });

  useEffect(() => {
    const getLinkedAccounts = async () => {
      try {
        const response = await axiosInstance.get(URL.linkedAccounts);
        const accounts = response?.data?.data || [];
        setLinkedAccounts(accounts);
        if (accounts.length === 0) setShowAddAccount(true);
        else if (accounts.length > 0) setShowAddAccount(false);
      } catch (error) {
        Bugsnag.notify(error);
        dispatch(
          addToast({
            msg: "Error fetching linked accounts",
            appearance: "error",
            autoDismiss: true,
          })
        );
        setShowAddAccount(true);
      } finally {
        setLoading(false);
      }
    };
    getLinkedAccounts();
  }, [reloadAccounts]);

  useEffect(() => {
    if (!reloadLinkedAccounts) return;
    setReloadAccounts(value => !value);
    setQueryParams({ reloadLinkedAccounts: null }, "replaceIn");
  }, [reloadLinkedAccounts]);

  const getCredentials = async authCode => {
    try {
      if (!googleClientId || !googleClientSecret) return;

      const response = await axios.post(URL.authToken, {
        client_id: googleClientId,
        client_secret: googleClientSecret,
        code: authCode,
        grant_type: "authorization_code",
        redirect_uri: websiteURL,
      });
      return response.data;
    } catch (error) {
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Error linking account",
          appearance: "error",
          autoDismiss: true,
        })
      );
    }
  };

  const getUserInfo = async token => {
    try {
      const response = await axios.get(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        { headers: { authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Error fetching account info",
          appearance: "error",
          autoDismiss: true,
        })
      );
    }
  };

  const addAccount = async (userInfo, credentials) => {
    try {
      if (!credentials?.refresh_token) {
        Bugsnag.notify(
          "Google Refresh Token Error: refresh_token missing in LinkedAccountsModal"
        );
      }

      const expirationTime = dayjs().add(credentials.expires_in, "second");
      await axiosInstance.post(URL.addAccount, {
        user_info: userInfo,
        credentials: { ...credentials, expirationTime },
      });
      dispatch(
        addToast({
          msg: "Account added successfully",
          appearance: "success",
          autoDismiss: true,
        })
      );
      setQueryParams({ reloadLinkedAccounts: 1 }, "replaceIn");
    } catch (error) {
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Error adding account",
          appearance: "error",
          autoDismiss: true,
        })
      );
    }
  };

  const closeModal = () => {
    setQueryParams({ showLinkedAccountsModal: null }, "replaceIn");
    const showLinkedModalTooltip = JSON.parse(
      sessionStorage.getItem("showLinkedModalTooltip")
    );
    if (showLinkedModalTooltip) {
      dispatch(setShowLinkedAccountTooltip(true));
      setTimeout(() => {
        dispatch(setShowLinkedAccountTooltip(false));
      }, 3000);
      sessionStorage.setItem("showLinkedModalTooltip", false);
    }
  };

  const toggleRemoveCta = value => setShowRemoveCta(value);

  const toggleRemoveAccountModal = () =>
    setShowRemoveAccountModal(!showRemoveAccountModal);

  const handleAddAccount = () => {
    trackEvent({
      event: "twa_db_calendar_add_account",
      payload: {
        provider_name: user?.name,
        provider_uuid: user?.uuid,
        provider_type: user?.usertype,
      },
    });
    googleLogin();
  };

  return (
    <>
      {showRemoveAccountModal && (
        <RemoveAccountModal
          userAccount={linkedAccounts[0]}
          toggleRemoveAccountModal={toggleRemoveAccountModal}
        />
      )}

      <Modal XS borderRadius="1rem">
        <Header>
          <H3 bold>Linked Accounts</H3>
          <Close id="close-linked-accounts" onClick={closeModal} />
        </Header>
        <Content>
          <Switch>
            <Case condition={loading}>
              <Loader />
            </Case>

            <Case condition={showAddAccount}>
              <FlexWrapper width="22rem">
                <FlexBox columnGap="0.5rem" align="center">
                  <img src={googleLogo} alt="Google Logo" width={40} />
                  <img src={link} alt="Link" width={24} height={24} />
                  {isCfProvider ? (
                    <img
                      src={childrenFirstLogo}
                      alt="Children First Logo"
                      height={40}
                    />
                  ) : (
                    <img src={amahaLogo} alt="Amaha Logo" width={40} />
                  )}
                </FlexBox>
                <Body2 bold>Add Google Account</Body2>
                <Support color={DAVYS_GREY_700}>
                  Linking your {isCfProvider ? "Children First" : "Amaha"}{" "}
                  google account will help you keep track of your other meetings
                  along with your regular sessions.
                </Support>
                <Support color={DAVYS_GREY_700}>
                  {isCfProvider ? "Children First" : "Amaha Professional"}'s use
                  and transfer of information received from Google APIs to any
                  other app will adhere to{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements.
                </Support>
                <IconButton
                  onClick={handleAddAccount}
                  Icon={FiPlus}
                  strokeWidth={3}
                >
                  ADD ACCOUNT
                </IconButton>
              </FlexWrapper>
            </Case>

            <Case condition={linkedAccounts.length > 0}>
              {linkedAccounts.map(({ user_info }) => (
                <FlexBox
                  width="100%"
                  columnGap="0.5rem"
                  onMouseEnter={() => toggleRemoveCta(true)}
                  onMouseLeave={() => toggleRemoveCta(false)}
                >
                  <AccountAvatar name={user_info?.name} />
                  <AccountDetails>
                    <Body2 bold>{user_info?.name}</Body2>
                    <Support color={DAVYS_GREY_700}>{user_info?.email}</Support>
                  </AccountDetails>
                  {showRemoveCta && (
                    <Button
                      textCta
                      color={ERROR_RED_500}
                      onClick={toggleRemoveAccountModal}
                    >
                      REMOVE
                    </Button>
                  )}
                </FlexBox>
              ))}
            </Case>
          </Switch>
        </Content>
      </Modal>
    </>
  );
};

export default LinkedAccountsModal;
